import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { isPast } from 'date-fns'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'
import DatePicker from '../../../../../../components/DatePicker'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { useLineItemDatesLimits } from '../../../useLineItemDatesLimits'
import { formatDateBE } from '../../../../../../constants/dates'

import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'
import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'

import { END_DATE, START_DATE } from '../../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import { DV_360_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { getInitialValues } from './fields'

function DatesSectionForm({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const campaign = useSelector(campaignSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const initialValues = useMemo(() => {
    return getInitialValues(item)
  }, [item])

  const handleSubmit = useCallback(
    values => {
      const startDate = formatDateBE(new Date(values[START_DATE]))
      const endDate = formatDateBE(new Date(values[END_DATE]))

      const updateData = {
        account: selectedAdAccountId,
        flight: {
          date_range: {
            start_date: startDate,
            end_date: endDate
          }
        },
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateData, item.id, DV_360_PLATFORM))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = formik

  const isStartDateInPast = isPast(new Date(values[START_DATE]))

  const { minStartDate, maxEndDate } = useLineItemDatesLimits({ campaign })

  const handleStartDatePickerChange = useCallback(
    value => {
      setFieldValue(START_DATE, value)
    },
    [setFieldValue]
  )

  const handleStopDatePickerChange = useCallback(
    value => {
      setFieldValue(END_DATE, value)
    },
    [setFieldValue]
  )

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || lineItemWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <FieldLabelWrapper label={t('Start Date')}>
        <DatePicker
          placeholder={t('Start Date')}
          value={values[START_DATE]}
          changeHandler={handleStartDatePickerChange}
          disabled={isStartDateInPast}
          minDate={minStartDate}
          maxDate={maxEndDate}
        />
      </FieldLabelWrapper>

      <FieldLabelWrapper label={t('End Date')}>
        <DatePicker
          title={t('End Date')}
          value={values[END_DATE]}
          changeHandler={handleStopDatePickerChange}
          minDate={isStartDateInPast ? new Date() : values[START_DATE]}
          maxDate={maxEndDate}
        />
      </FieldLabelWrapper>
    </EditForm>
  )
}

export default DatesSectionForm
